"use client";

import {
  type FC,
  Fragment,
  type HTMLAttributes,
  useRef,
  useState,
} from "react";
import { AnimatePresence, motion } from "motion/react";
import { format, formatDistanceToNow, isToday } from "date-fns";
import { nl } from "date-fns/locale";
import { mergeClasses, rem } from "@huub-web/utils";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";

import { NavigationContentHeader } from "../NavigationContentHeader";
import { Ruler } from "../Ruler";
import { Text } from "../Text";
import { Flex } from "../Flex";
import { ScrollPane } from "../ScrollPane";
import { Placeholder } from "../Placeholder";
import { IconButton } from "../IconButton";
import { Tooltip } from "../Tooltip";

import { getPluralOrSingularTranslation } from "../../helpers";
import { useChatHistory, useMaybePubSub, useOnce } from "../../hooks";
import type { ChatHistoryOverviewItem, ChatHistoryOverview } from "../../types";

import { HistorySet } from "./components/HistorySet";
import { HistoryChat } from "./components/HistoryChat";
import { HistorySearchModal } from "./components/HistorySearchModal";

import {
  contentMotionVariants,
  contentChildMotionVariants,
} from "./ChatHistoryList.motion";
import {
  containerStyle,
  noChatMessageStyle,
  scrollContentStyle,
  searchButtonStyle,
} from "./ChatHistoryList.css";

export type ChatHistoryListProps = HTMLAttributes<HTMLDivElement> & {
  active?: boolean;
  search?: boolean;
  variant?: "compact" | "default" | "sideContent";
  onChatClick?: (item: ChatHistoryOverviewItem) => void;
};

type GroupedHistoryList = {
  [key: string]: { label: string; items: ChatHistoryOverviewItem[] };
};

/**
 * ChatHistory component is used to display the main copy content of a page.
 */
export const ChatHistoryList: FC<ChatHistoryListProps> = ({
  active = true,
  search = false,
  variant = "default",
  className,
  onChatClick,
}) => {
  const { t } = useTranslation();
  const { fetchConversationHistory, activeConversationID } = useChatHistory();
  const pubSub = useMaybePubSub();

  const scrollRef = useRef<HTMLDivElement>(null);

  const [data, setData] = useState<ChatHistoryOverview>([]);
  const groupedData: GroupedHistoryList = groupByDate(data ?? [], t);

  const [loading, setLoading] = useState(true);

  useOnce(() => {
    setLoading(true);

    fetchConversationHistory(1, 10)
      .then((data) => {
        setData(data.data ?? []);
      })
      .catch((error) => {
        console.error(error);
        pubSub?.publish("snackbar", {
          body: t("general.error"),
          variant: "error",
        });
      })
      .finally(() => {
        setTimeout(() => setLoading(false), 500);
      });
  });

  const [searchModalOpen, setSearchModalOpen] = useState(false);

  const handleSearchClick = () => {
    setSearchModalOpen(true);
  };

  const handleChatClick = (item: ChatHistoryOverviewItem) => {
    onChatClick?.(item);
  };

  return (
    <motion.div
      className={mergeClasses(containerStyle({ active }), className)}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <NavigationContentHeader
        title={t("history.title")}
        subtitle={
          <Text variant="bodyS" color="currentColor50" italic={loading}>
            {loading
              ? t("general.loading")
              : t("history.totalChats", {
                  total: data?.length ?? 0,
                  label: getPluralOrSingularTranslation(
                    data?.length ?? 0,
                    t("general.chats.singular"),
                    t("general.chats.plural"),
                  ),
                })}
          </Text>
        }
        variant={variant}
        trailing={
          search && (
            <Tooltip value={t("general.search")} placement="left">
              <IconButton
                onClick={handleSearchClick}
                className={searchButtonStyle}
                icon="search"
                size="xxs"
                variant="opaque"
                rounded
              />
            </Tooltip>
          )
        }
      />

      <ScrollPane
        ref={scrollRef}
        overlapColor={variant === "compact" ? undefined : "navigation"}
        hideScrollbar
      >
        {loading && (
          <Flex
            direction="column"
            className={scrollContentStyle({ variant })}
            gap="s"
          >
            <div />
            <Placeholder variant="loadingShade" height={rem(16)} width="60%" />
            <Placeholder variant="loadingShade" height={rem(26)} />
            <Placeholder variant="loadingShade" height={rem(26)} />
            <Placeholder variant="loadingShade" height={rem(26)} />
          </Flex>
        )}

        <AnimatePresence>
          {!loading && (
            <motion.div
              variants={contentMotionVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              className={scrollContentStyle({ variant, gap: true })}
            >
              {Object.keys(groupedData).map((id, i) => (
                <Fragment key={id}>
                  {i !== 0 && (
                    <Ruler
                      variants={contentChildMotionVariants}
                      variant="border"
                    />
                  )}
                  <HistorySet title={groupedData[id].label}>
                    {groupedData[id].items.map((item) => (
                      <HistoryChat
                        onClick={() => handleChatClick(item)}
                        key={item.id}
                        title={item.title}
                        active={activeConversationID === item.id}
                      />
                    ))}
                  </HistorySet>
                </Fragment>
              ))}

              {(data?.length ?? 0) === 0 && (
                <Text
                  variant="bodyS"
                  color="currentColor50"
                  className={noChatMessageStyle}
                  italic
                >
                  {t("history.empty")}
                </Text>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </ScrollPane>

      {searchModalOpen && (
        <HistorySearchModal onClose={() => setSearchModalOpen(false)} />
      )}
    </motion.div>
  );
};

const groupByDate = (list: ChatHistoryOverview = [], t: TFunction) => {
  const grouped: GroupedHistoryList = {};

  list.forEach((item) => {
    const date = new Date(item.created);
    const key = format(date, "yyyy-MM-dd");

    const label = isToday(date)
      ? t("general.today")
      : formatDistanceToNow(date, {
          addSuffix: true,
          locale: nl,
        });

    if (!grouped[key]) {
      grouped[key] = { items: [], label };
    }

    grouped[key].items.push(item);
  });

  return grouped;
};
